import React, { useState, useEffect, useCallback } from "react";
import { ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import SocialShareDropdown from "./SocialShareDropdown";

interface PredictionResultCardProps {
  predictionTarget: {
    id: string;
    target_code: string;
    yes_image_url: string;
    no_image_url: string;
    event?: {
      id: string;
      title: string;
      description?: string; // Stores the specific market condition
      close_time: string;
      yes_outcome_label: string;
      no_outcome_label: string;
      kalshi_event_id?: string;
      resolved_outcome?: string;
      metadata?: {
        market_ticker?: string;
        market_condition?: string;
      };
    };
  };
  userPrediction: 'YES' | 'NO';
  /** Optional override used in historic views when is_correct is known but event.resolved_outcome may be null. */
  resultStatus?: 'correct' | 'incorrect';
}


interface MarketData {
  yes: { ticker: string; last_price: number };
  no: { ticker: string; last_price: number };
}

interface MarketMetadata {
  market_ticker?: string;
}

const PredictionResultCard: React.FC<PredictionResultCardProps> = ({
  predictionTarget,
  userPrediction,
  resultStatus,
}) => {
  const [marketData, setMarketData] = useState<MarketData | null>(null);
  const [kalshiUrl, setKalshiUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const event = predictionTarget.event;

  // Format close date/time with year - this is when the market closes for betting
  const closeDateTime = event?.close_time 
    ? `Closes ${new Date(event.close_time).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short'
      })}`
    : 'Close time unknown';

  const loadMarketData = useCallback(async () => {
    const eventId = event?.id;
    if (!eventId) {
      if (import.meta.env.DEV) console.log('No event ID for market data');
      return;
    }
    
    try {
      if (import.meta.env.DEV) console.log('Loading market data for event:', eventId);
      const { data, error } = await supabase.functions.invoke('get-kalshi-market', {
        body: { eventId },
      });

      if (import.meta.env.DEV) console.log('Market data response:', data, 'error:', error);

      if (!error && data) {
        if (data.marketData) {
          if (import.meta.env.DEV) console.log('Setting market data:', data.marketData);
          setMarketData(data.marketData);
        }
        if (data.kalshiUrl) {
          setKalshiUrl(data.kalshiUrl);
        }
      }
    } catch (err) {
      console.error('Error loading market data:', err);
    }
  }, [event?.id]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await loadMarketData();
      setIsLoading(false);
    };
    loadData();
  }, [loadMarketData]);

  const getShareContent = () => {
    return `I made a prediction on "${event?.title}" using RemoteViz! My pick: ${userPrediction === 'YES' ? event?.yes_outcome_label : event?.no_outcome_label}`;
  };

  // Get the specific market condition (e.g., "Over 5.5 goals scored", "Tom Wilson 2+")
  const marketCondition = event?.description || (event?.metadata as any)?.market_condition;
  
  // Determine if event is resolved and if prediction was correct
  const resolvedOutcome = event?.resolved_outcome?.toUpperCase();
  const isResolved = !!resultStatus || !!resolvedOutcome;
  const isCorrect = resultStatus
    ? resultStatus === 'correct'
    : isResolved && resolvedOutcome === userPrediction;
  const isIncorrect = resultStatus
    ? resultStatus === 'incorrect'
    : isResolved && resolvedOutcome !== userPrediction;

  // Get styling for the selected option based on resolution status
  const getSelectedOptionStyle = (isSelected: boolean) => {
    if (!isSelected) return 'bg-muted/30';
    if (isCorrect) return 'bg-success/15 ring-1 ring-success/60';
    if (isIncorrect) return 'bg-destructive/15 ring-1 ring-destructive/60';
    return 'bg-primary/10 ring-1 ring-primary/60';
  };

  const getCheckmarkStyle = () => {
    if (isCorrect) return 'text-success';
    if (isIncorrect) return 'text-destructive';
    return 'text-primary';
  };
  
  return (
    <>
      <div className="bg-card rounded-lg p-4 space-y-3 border border-border">
        {/* Event Title and Resolved Badge */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <h3 className="font-semibold text-foreground text-base">
              {event?.title || "Prediction Event"}
            </h3>
            {/* Show specific market condition if different from title */}
            {marketCondition && marketCondition !== event?.title && (
              <p className="text-sm text-primary font-medium mt-1">
                {marketCondition}
              </p>
            )}
          </div>
          {isResolved && (
            <span
              className={`shrink-0 text-xs font-medium px-2 py-1 rounded-full border ${
                isCorrect
                  ? 'bg-success/15 text-success border-success/30'
                  : 'bg-destructive/15 text-destructive border-destructive/30'
              }`}
            >
              {isCorrect ? '✓ Correct' : '✗ Incorrect'}
            </span>
          )}
        </div>

        {/* Options Table */}
        <div className="space-y-2">
          {/* YES Option Row */}
          <div className={`grid grid-cols-[48px_1fr_auto_60px] gap-2 items-center p-2 rounded-md ${
            getSelectedOptionStyle(userPrediction === 'YES')
          }`}>
            <div className="w-12 h-12 rounded overflow-hidden">
              <img 
                src={predictionTarget.yes_image_url} 
                alt="Option A"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-sm font-medium text-foreground truncate">
              {event?.yes_outcome_label || "Option A"}
            </span>
            <div className="flex items-center">
              {userPrediction === 'YES' && (
                <span className={`text-xs font-medium ${getCheckmarkStyle()}`}>✓</span>
              )}
            </div>
            <span className="text-sm text-center text-muted-foreground">
              {typeof marketData?.yes?.last_price === 'number'
                ? `${marketData.yes.last_price}¢` 
                : '--'}
            </span>
          </div>

          {/* NO Option Row */}
          <div className={`grid grid-cols-[48px_1fr_auto_60px] gap-2 items-center p-2 rounded-md ${
            getSelectedOptionStyle(userPrediction === 'NO')
          }`}>
            <div className="w-12 h-12 rounded overflow-hidden">
              <img 
                src={predictionTarget.no_image_url} 
                alt="Option B"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-sm font-medium text-foreground truncate">
              {event?.no_outcome_label || "Option B"}
            </span>
            <div className="flex items-center">
              {userPrediction === 'NO' && (
                <span className={`text-xs font-medium ${getCheckmarkStyle()}`}>✓</span>
              )}
            </div>
            <span className="text-sm text-center text-muted-foreground">
              {typeof marketData?.no?.last_price === 'number'
                ? `${marketData.no.last_price}¢` 
                : '--'}
            </span>
          </div>
        </div>

        {/* Footer Row */}
        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-muted-foreground">
            {closeDateTime}
          </span>
          <div className="flex items-center gap-2">
            <SocialShareDropdown content={getShareContent()} />
            {kalshiUrl && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      asChild
                    >
                      <a href={kalshiUrl} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>View on Kalshi</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PredictionResultCard;
